import { useEffect, useMemo, useState } from "react";
import { FileText, MoreHorizontal, PencilIcon, PlusIcon, ShieldCheck, TrashIcon } from "lucide-react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import { usePermissions } from "@/lib/permissions";
import { PageContainer, PageHeader } from "@/components/PagePrimitives";
import { EmptyState, ErrorState, HeaderRow, LoadingState, TH, TableShell } from "@/components/PageUi";
import { InfoHint } from "@/components/InfoHint";
import { ExpenseApprovalPolicyFormDialog } from "@/components/ExpenseApprovalPolicyFormDialog";
import {
  useExpensePolicyReferenceData,
  type ExpensePolicyReferenceData,
} from "@/components/ExpenseApprovalPolicyFields";
import { ExpensePolicyDocumentsPanel } from "@/components/ExpensePolicyDocumentsPanel";
import {
  clausePageLabel,
  draftFromPolicy,
  expenseActionBadgeClass,
  expenseActionLabel,
  expenseConditionSummary,
  toUpsertRequest,
  versionLabel,
  type ExpenseApprovalPolicyResponse,
} from "@/lib/expense-approval-policy-utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/** Approver names shown before collapsing to "+N". */
const MAX_APPROVERS_SHOWN = 3;
/** Merchant/card pills shown inline in "Applies to" before collapsing to "+N more". */
const MAX_SCOPE_PILLS = 4;

export interface ExpenseApprovalPoliciesPageProps {
  /** Opens a policy document's review screen (from the document panel or a policy's source marker). */
  onOpenDocument?: (documentId: string) => void;
  onMerchantClick?: (merchantId: string) => void;
}

/** Nothing selected is a policy for all spend. */
function AppliesToCell({
  policy,
  referenceData: ref,
  onMerchantClick,
}: {
  policy: ExpenseApprovalPolicyResponse;
  referenceData: ExpensePolicyReferenceData;
  onMerchantClick?: (merchantId: string) => void;
}) {
  const pills = [
    ...policy.merchantIds.map((id) => ({ kind: "merchant" as const, id, label: ref.merchantName(id) })),
    ...policy.cardConnectionIds.map((id) => ({ kind: "card" as const, id, label: ref.cardName(id) })),
    ...(policy.spenderUserIds ?? []).map((id) => ({ kind: "person" as const, id, label: ref.userLabel(id) })),
  ];
  // Unscoped is the default; saying "All spend" on every row is noise. Only a scoped policy shows its scope.
  if (pills.length === 0) return null;
  const shown = pills.slice(0, MAX_SCOPE_PILLS);
  const overflow = pills.length - shown.length;
  return (
    <div className="mt-1.5 flex flex-wrap gap-1">
      {shown.map((p) =>
        p.kind === "merchant" && onMerchantClick ? (
          <Badge key={`m-${p.id}`} variant="outline" asChild className="cursor-pointer font-normal hover:bg-muted">
            <button type="button" onClick={() => onMerchantClick(p.id)}>
              {p.label}
            </button>
          </Badge>
        ) : (
          <Badge key={`${p.kind}-${p.id}`} variant="outline" className="font-normal">
            {p.label}
          </Badge>
        ),
      )}
      {overflow > 0 && (
        <Badge variant="secondary" className="font-normal text-muted-foreground">
          +{overflow} more
        </Badge>
      )}
    </div>
  );
}

/** Who a Require approval policy routes to, by name. The other actions decide on their own. */
function ApproversCell({
  policy,
  referenceData: ref,
}: {
  policy: ExpenseApprovalPolicyResponse;
  referenceData: ExpensePolicyReferenceData;
}) {
  if (policy.action === "AutoApprove") return null;
  if (policy.action === "Flag") {
    return <span className="block text-xs text-muted-foreground">An admin resolves</span>;
  }
  const ids = policy.approverUserIds ?? [];
  if (ids.length === 0) {
    return <span className="block text-xs text-muted-foreground">Anyone who can approve</span>;
  }
  const names = ids.map((id) => ref.userLabel(id));
  const shown = names.slice(0, MAX_APPROVERS_SHOWN);
  const overflow = names.length - shown.length;
  return (
    <span className="block truncate text-xs text-muted-foreground">
      Waits for <span className="text-foreground">{shown.join(", ")}</span>
      {overflow > 0 ? <span className="text-muted-foreground">{` +${overflow}`}</span> : null}
    </span>
  );
}

/** Shows the quoted clause a document-sourced policy enforces, to check the rule against its words. */
export function PolicySourceMarker({
  clause,
  sourceDocumentId,
  onOpenDocument,
}: {
  clause?: ExpenseApprovalPolicyResponse["clause"];
  sourceDocumentId?: string | null;
  onOpenDocument?: (documentId: string) => void;
}) {
  if (!sourceDocumentId && !clause) return null;
  const page = clausePageLabel(clause);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-md text-xs text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
        >
          <FileText className="size-3" />
          From policy document
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 space-y-2 text-sm">
        {clause ? (
          <blockquote className="border-l-2 border-accent-blue/50 pl-3 text-pretty italic">
            &ldquo;{clause.quote}&rdquo;
          </blockquote>
        ) : (
          <p className="text-muted-foreground">Created from the uploaded expense policy.</p>
        )}
        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>{page ? `Policy document, ${page}` : "Policy document"}</span>
          {sourceDocumentId && onOpenDocument ? (
            <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => onOpenDocument(sourceDocumentId)}>
              Open document
            </Button>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Mutations are gated by `manageExpensePolicies`; the backend 403 is the real enforcement. */
export function ExpenseApprovalPoliciesPage({ onOpenDocument, onMerchantClick }: ExpenseApprovalPoliciesPageProps) {
  const api = useDolfinClient();
  const { can } = usePermissions();
  const canManage = can("manageExpensePolicies");
  const [policies, setPolicies] = useState<ExpenseApprovalPolicyResponse[]>([]);
  // The members and merchants these policies name, so any beyond the first page are fetched by id.
  const referencedIds = useMemo(
    () => ({
      userIds: policies.flatMap((p) => [...(p.approverUserIds ?? []), ...(p.spenderUserIds ?? [])]),
      merchantIds: policies.flatMap((p) => p.merchantIds ?? []),
    }),
    [policies],
  );
  const referenceData = useExpensePolicyReferenceData(referencedIds);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ExpenseApprovalPolicyResponse | null>(null);
  const [policyToDelete, setPolicyToDelete] = useState<ExpenseApprovalPolicyResponse | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  async function fetchPolicies() {
    setLoading(true);
    setError(null);
    const { data, error: apiError } = await api.GET("/v1/expenses/approval-policies", {});
    if (apiError) {
      setError(apiErrorMessage(apiError, "Couldn't load your expense policies."));
    } else {
      setPolicies(data ?? []);
    }
    setLoading(false);
  }

  useEffect(() => {
    fetchPolicies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function confirmDelete() {
    const target = policyToDelete;
    if (!target) return;
    setPolicyToDelete(null);
    const { error: apiError } = await api.DELETE("/v1/expenses/approval-policies/{id}", {
      params: { path: { id: target.id } },
    });
    if (apiError) {
      toast.error(apiErrorMessage(apiError, "Failed to delete the expense policy"));
      return;
    }
    toast.success("Expense policy deleted");
    setPolicies((prev) => prev.filter((p) => p.id !== target.id));
  }

  // Full-update PUT to flip `active` - the upsert body is the whole policy.
  async function toggleActive(policy: ExpenseApprovalPolicyResponse, next: boolean) {
    setTogglingId(policy.id);
    const { data, error: apiError } = await api.PUT("/v1/expenses/approval-policies/{id}", {
      params: { path: { id: policy.id } },
      body: toUpsertRequest({ ...draftFromPolicy(policy), active: next }),
    });
    setTogglingId(null);
    if (apiError || !data) {
      toast.error(apiErrorMessage(apiError, "Failed to update the expense policy"));
      return;
    }
    setPolicies((prev) => prev.map((p) => (p.id === data.id ? data : p)));
  }

  // Priority order, so the table reads top to bottom the way an expense is checked. Scoped policies win
  // over global ones regardless of priority; that is explained in the hint rather than re-sorted here.
  const ordered = [...policies].sort((a, b) => Number(a.priorityOrder) - Number(b.priorityOrder));

  const nextPriority = policies.length
    ? Math.min(1000, Math.max(...policies.map((p) => Number(p.priorityOrder))) + 10)
    : 100;

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Expenses"
        title="Approval policies"
        subtitle="Rules that decide what happens to a card expense once it is coded - post it straight away, hold it for approval, or flag it as breaking your policy."
        actions={
          canManage ? (
            <Button onClick={openCreate} className="w-full gap-1.5 sm:w-auto">
              <PlusIcon className="size-4" />
              New policy
            </Button>
          ) : undefined
        }
      />

      <div className="flex flex-col gap-6">
        <div>
          {loading ? (
            <LoadingState label="Loading expense policies..." />
          ) : error ? (
            <ErrorState message={error} onRetry={fetchPolicies} />
          ) : policies.length === 0 ? (
            <EmptyState
              icon={ShieldCheck}
              title="No expense policies yet"
              action={
                canManage ? (
                  <Button size="sm" onClick={openCreate}>
                    <PlusIcon className="size-4" /> New policy
                  </Button>
                ) : undefined
              }
            >
              Expenses follow your review-before-posting setting. Add a policy to auto-approve small spend, name
              who approves what, or flag spend your policy does not allow. Or upload your written policy below.
            </EmptyState>
          ) : (
            <>
              <TableShell>
                <Table>
                  <TableHeader>
                    <HeaderRow>
                      <TableHead className={`w-[84px] ${TH}`}>
                        <span className="inline-flex items-center gap-1">
                          Priority
                          <InfoHint label="priority">
                            A policy for particular merchants, cards or people is checked before one for all spend. Within
                            each, lower numbers are checked first, and the first policy an expense matches decides
                            the outcome.
                          </InfoHint>
                        </span>
                      </TableHead>
                      <TableHead className={TH}>Name</TableHead>
                      <TableHead className={TH}>Conditions</TableHead>
                      <TableHead className={`w-[220px] ${TH}`}>Outcome</TableHead>
                      <TableHead className={`w-[80px] ${TH}`}>Active</TableHead>
                      <TableHead className="w-[48px]" />
                    </HeaderRow>
                  </TableHeader>
                  <TableBody>
                    {ordered.map((p) => (
                      <TableRow key={p.id} className={p.active ? undefined : "opacity-60"}>
                        <TableCell className="font-mono text-xs text-muted-foreground tabular-nums">
                          {p.priorityOrder}
                        </TableCell>
                        <TableCell className="min-w-[200px] whitespace-normal">
                          <div className="flex items-center gap-1.5">
                            <span className="font-semibold">{p.name}</span>
                            <Badge variant="secondary" className="px-1.5 font-mono text-[10px] font-normal">
                              {versionLabel(p.version)}
                            </Badge>
                          </div>
                          <PolicySourceMarker
                            clause={p.clause}
                            sourceDocumentId={p.sourceDocumentId}
                            onOpenDocument={onOpenDocument}
                          />
                        </TableCell>
                        <TableCell className="whitespace-normal text-sm text-muted-foreground">
                          {expenseConditionSummary(p, referenceData.categoryLabel)}
                          <AppliesToCell policy={p} referenceData={referenceData} onMerchantClick={onMerchantClick} />
                        </TableCell>
                        <TableCell className="max-w-[220px] space-y-1">
                          <Badge className={`text-xs font-normal ${expenseActionBadgeClass(p.action)}`}>
                            {expenseActionLabel(p.action)}
                          </Badge>
                          <ApproversCell policy={p} referenceData={referenceData} />
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={p.active}
                            disabled={!canManage || togglingId === p.id}
                            onCheckedChange={(next) => toggleActive(p, next)}
                            aria-label={`Toggle ${p.name} active`}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          {canManage ? (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="size-8"
                                  aria-label={`Actions for ${p.name}`}
                                >
                                  <MoreHorizontal className="size-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-40">
                                <DropdownMenuItem
                                  onClick={() => {
                                    setEditing(p);
                                    setFormOpen(true);
                                  }}
                                >
                                  <PencilIcon className="mr-2 size-4" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  onClick={() => setPolicyToDelete(p)}
                                >
                                  <TrashIcon className="mr-2 size-4" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableShell>
              {/* The fall-through is the rule people forget exists, so it is stated where the list ends. */}
              <p className="pt-3 text-xs text-muted-foreground">
                An expense that matches none of the active policies follows your review-before-posting setting.
              </p>
            </>
          )}
        </div>

        <ExpensePolicyDocumentsPanel onOpenDocument={onOpenDocument} />
      </div>

      <ExpenseApprovalPolicyFormDialog
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) setEditing(null);
        }}
        policy={editing}
        defaultPriority={nextPriority}
        referenceData={referenceData}
        onSaved={fetchPolicies}
      />

      <AlertDialog open={policyToDelete !== null} onOpenChange={(open) => !open && setPolicyToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete expense policy?</AlertDialogTitle>
            <AlertDialogDescription>
              &ldquo;{policyToDelete?.name}&rdquo; will be permanently removed. Expenses coded afterwards will be
              checked against the remaining policies.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={confirmDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
